import React, { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import PropTypes from "prop-types";
import {
  Box,
  Divider,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Card from "../../components/Cards/Card";
import NativeObject from "../../components/NativeObject/NativeObject";
import LayoutWrapper from "../../components/LayoutWrapper/LayoutWrapper";
import Kpi from "../../components/KPI/Kpi";
import { useSelections } from "../../context/Selections";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import utils from "../../QlikUtils";
import ObjectLoader from "../../components/ObjectLoader/ObjectLoader";
import Bar from "../../components/Bar/Bar";

function ExtraScoresDesktop({ app, config }) {
  const notFullHd = useMediaQuery({ query: "(max-width: 1536px)" });
  const { selections } = useSelections();
  const [loading, setLoading] = useState(true);
  const [openReports, setOpenReports] = useState(false);
  const [goalValue, value, percentage] = [
    Kpi({
      app,
      objectId: "a2140dca-ca97-447b-a197-a5f3996ec93b",
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: "08cc2c04-721d-4d1a-a797-27f13dea37aa",
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: "0dd66ba6-fef5-43cb-a85e-44fd2566ed5a",
      updatesWithSelection: true,
    }),
  ];

  const [percentageNum, setPercentageNum] = useState(percentage);
  const [percentageColor, setPercentageColor] = useState(null);
  const [rankingRows, setRankingRows] = useState(null);
  const [highestValue, setHighestValue] = useState(null);
  const [scale, setScale] = useState(null);
  const [textQuarter, setTextQuarter] = useState();

  const initializeRows = async (id) => {
    let rows = [];
    await utils.createHyperCubeByQlikIdToTable(app, id, 100, async (res) => {
      rows = await utils
        .extractDataSet(res)
        .slice(1)
        .map((item) => {
          // convert names to title case
          const name = item[0].value?.split(" ");
          for (var i = 0; i < name.length; i++) {
            name[i] =
              name[i].charAt(0).toUpperCase() + name[i].slice(1).toLowerCase();
          }
          return {
            name: name.join(" "),
            scoreValue: parseInt(item[1].value),
            goalValue: parseInt(item[2].value.slice(2).replace(/\./g, "")),
            value: parseInt(item[3].value.slice(2).replace(/\./g, "")),
          };
        });
      const a = rows
        .map((e) => Math.max(e.value, e.goalValue))
        .reduce(function (prev, current) {
          return Math.max(prev, current);
        });
      const highestValue = a;
      const roundedUpScale =
        Math.round(a / 10 ** (a.toString().length - 1)) *
        10 ** (a.toString().length - 1);
      const scale = [0, roundedUpScale / 2, roundedUpScale];
      setRankingRows(rows);
      setHighestValue(highestValue);
      setScale(scale);
      setLoading(false);
      return;
    });
  };
  useEffect(() => {
    initializeRows("WjtK");
  }, []);

  const getColorFromPercentage = (percentage) => {
    switch (true) {
      case percentage < 85:
        return "#E95248";
      case percentage < 100:
        return "#FDB949";
      default:
        return "#38BC9A";
    }
  };

  const fetchKPIValue = useCallback(
    async (objectId, position) => {
      if (app && objectId) {
        let id = objectId;
        await app.visualization.get(id).then((res) => {
          const value =
            res?.model?.layout?.qHyperCube?.qGrandTotalRow[0]?.qText ??
            res?.model?.layout?.qHyperCube?.qGrandTotalRow[0]?.qNum ??
            null;
          setTextQuarter(value && value[0] !== "-" ? value : null);
        });
      }
    },
    [app, setTextQuarter]
  );

  useEffect(() => {
    if (app) {
      const num = parseInt(percentage?.slice(0, -1)) ?? 0;
      const percentageNum = num > 100 ? 100 : num;
      const percentageColor = getColorFromPercentage(percentageNum);
      setPercentageNum(percentageNum);
      setPercentageColor(percentageColor);

      fetchKPIValue("jzfBCy");
    }
  }, [selections]);

  return (
    <div className="app extraScores">
      <div className="appContent">
        <LayoutWrapper>
          <Grid
            container
            flexDirection={"column"}
            justifyContent={"space-between"}
            alignItems={"start"}
            className="extra-scores-wrapper"
            rowGap={2}
          >
            {/* TITLE + VIEW TOGGLE */}
            <Grid
              item
              container
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Grid
                item
                display={"flex"}
                alignItems={"center"}
                gap={"10px"}
                className="titles-mobile"
              >
                <Box component={"h1"}>Ponto Extra - Sellout Total</Box>

                {!openReports && (
                  <>
                    {"-"}
                    <Grid
                      className="container-periodo"
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                      xs
                      item
                    >
                      <p>Período Analisado:</p>

                      <p
                        style={{
                          fontWeight: "bold",
                          fontSize: "17px",
                          color: "black",
                        }}
                      >
                        {textQuarter}
                      </p>
                    </Grid>
                  </>
                )}
              </Grid>

              <Grid item>
                <ToggleButtonGroup
                  color="primary"
                  value={openReports ? "Relatórios" : "Overview"}
                  exclusive
                  onChange={(e, value) => {
                    setOpenReports(value === "Relatórios");
                  }}
                  aria-label="Platform"
                >
                  <ToggleButton
                    size="small"
                    value="Overview"
                    className={`toggleButton`}
                  >
                    Overview
                  </ToggleButton>

                  <ToggleButton
                    size="small"
                    className={`toggleButton`}
                    value="Relatórios"
                  >
                    Relatórios
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>

            {/* PAGE CONTENT */}
            {!openReports ? (
              <Grid
                container
                flexDirection={"flex-row"}
                justifyContent={"flex-start"}
                columnGap={2}
              >
                <Grid
                  container
                  flexDirection={"column"}
                  justifyContent={"flex-start"}
                  rowGap={2}
                  item
                  xs
                  sx={{
                    height: "100%",
                  }}
                >
                  <Grid
                    item
                    xs
                    sx={{
                      height: "100%",
                    }}
                  >
                    <Card
                      app={app}
                      header={{
                        hardTitle: "Atingimento",
                      }}
                    >
                      {/* KPI PROGRESS BAR */}
                      <Grid
                        container
                        item
                        xs={12}
                        flexDirection={"column"}
                        justifyContent={"flex-start"}
                        alignItems={"flex-start"}
                        gap={1.5}
                        sx={{ padding: "0 0 12px", height: "100%" }}
                      >
                        <Grid
                          className={`value-wrapper`}
                          container
                          item
                          xs={12}
                          flexDirection={"column"}
                          justifyContent={"flex-start"}
                          alignItems={"flex-start"}
                          gap={0.5}
                        >
                          {/* VALUE */}
                          <Grid
                            container
                            item
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"flex-end"}
                            style={{
                              width: "100%",
                              marginBottom: "4px",
                            }}
                          >
                            <Box
                              component={"span"}
                              className="value"
                              style={{
                                color: percentageColor,
                              }}
                            >
                              {value}&nbsp;sellout realizado
                            </Box>
                          </Grid>

                          {/* PROGRESS BAR */}
                          <Grid
                            container
                            item
                            flexDirection={"column"}
                            justifyContent={"flex-start"}
                            alignItems={"flex-start"}
                            style={{
                              width: "100%",
                            }}
                          >
                            <ProgressBar
                              key={"a"}
                              bgColor={percentageColor}
                              completed={percentageNum}
                            />
                          </Grid>
                        </Grid>
                        {/* GOAL AND PERCENTAGE */}
                        <Grid
                          container
                          item
                          flexDirection={"row"}
                          justifyContent={"flex-start"}
                          alignItems={"flex-start"}
                          xs
                        >
                          {/* GOAL VALUE */}
                          <Grid
                            className={`goal-wrapper`}
                            container
                            item
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                            columnGap={notFullHd ? 0.6 : 1}
                            xs
                          >
                            <Grid item>
                              {/* VALOR EM CINZA */}
                              <Box component={"span"} className="goal-value">
                                {goalValue}
                              </Box>
                            </Grid>
                            <Grid item>
                              <Box component={"span"} className="goal-text">
                                Meta
                              </Box>
                            </Grid>
                          </Grid>

                          {/* GOAL PERCENTAGE */}
                          <Grid
                            className={`percentage-wrapper`}
                            container
                            item
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                            columnGap={notFullHd ? 0.6 : 1}
                            xs={4.5}
                          >
                            <Grid item>
                              <Box
                                component={"span"}
                                className="percentage-value"
                                style={{
                                  color: percentageColor,
                                }}
                              >
                                {percentage}
                              </Box>
                            </Grid>
                            <Grid item>
                              <Box
                                component={"span"}
                                className="percentage-text"
                              >
                                Atingimento
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs
                    sx={{
                      height: "100%",
                    }}
                  >
                    <Card
                      app={app}
                      header={{
                        hardTitle: "Scorecard Atingimento",
                      }}
                    >
                      <Grid
                        container
                        item
                        flexDirection={"column"}
                        justifyContent={"space-between"}
                        alignItems={"flex-start"}
                        xs
                        sx={{
                          height: "100%",
                        }}
                      >
                        <Grid
                          container
                          item
                          xs
                          sx={{
                            minHeight: notFullHd ? "45vh" : "52.8vh",
                            height: "100%",
                            width: "100%",
                          }}
                        >
                          <NativeObject
                            app={app}
                            key="JRTkMua"
                            reference={{
                              id: "JRTkMua",
                            }}
                            style={{
                              padding: "0 8px ",
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
                <Grid
                  container
                  flexDirection={"column"}
                  justifyContent={"flex-start"}
                  item
                  xs
                  sx={{
                    height: "100%",
                  }}
                >
                  <Card
                    app={app}
                    header={{
                      hardTitle: "Performance Distribuidor",
                      legends: [
                        {
                          type: "bar",
                          iconColor: "#F98561",
                          name: "Meta",
                        },
                        {
                          type: "bar",
                          iconColor: "#55B6EB",
                          name: "Realizado",
                        },
                      ],
                    }}
                  >
                    {loading || !rankingRows ? (
                      <Grid
                        item
                        xs
                        sx={{
                          height: notFullHd ? "69vh" : "73vh",
                        }}
                      >
                        <ObjectLoader
                          icon="table"
                          height="inherit"
                          width="inherit"
                        />
                      </Grid>
                    ) : (
                      <Grid
                        container
                        flexDirection={"row"}
                        justifyContent={"center"}
                        alignItems={"flex-start"}
                        rowGap={1}
                      >
                        <Grid
                          container
                          item
                          flexDirection={"row"}
                          justifyContent={"flex-start"}
                          sx={{
                            maxHeight: notFullHd ? "66vh" : "70.5vh",
                            overflowY: "scroll",
                          }}
                          xs={12}
                        >
                          <Grid
                            container
                            item
                            flexDirection={"column"}
                            justifyContent={"center"}
                            rowGap={2}
                            alignItems={"center"}
                            sx={{
                              height: "100%",
                              margin: "0 8px 0 4px",
                            }}
                            xs={12}
                          >
                            {rankingRows
                              ? rankingRows?.map((row, index) => (
                                  <Grid
                                    key={index}
                                    container
                                    flexDirection={"row"}
                                    justifyContent={"start"}
                                    alignItems={"center"}
                                    item
                                    columnGap={0.5}
                                    xs
                                  >
                                    <Grid
                                      container
                                      flexDirection={"row"}
                                      justifyContent={"start"}
                                      alignItems={"center"}
                                      item
                                      xs={0.5}
                                    >
                                      <Box
                                        component={"span"}
                                        className="rank-index"
                                      >
                                        {index + 1}
                                      </Box>
                                    </Grid>
                                    <Grid
                                      container
                                      item
                                      flexDirection={"column"}
                                      justifyContent={"center"}
                                      alignItems={"flex-start"}
                                      xs
                                      rowGap={0.8}
                                    >
                                      {/* Distributor title and score */}
                                      <Grid item xs>
                                        <Box
                                          className="score"
                                          component={"span"}
                                        >
                                          {`${row.scoreValue} pontos | ${row.name}`}
                                        </Box>
                                      </Grid>
                                      {/* Distributor goal and reached value */}
                                      <Grid
                                        item
                                        container
                                        flexDirection={"column"}
                                        justifyContent={"center"}
                                        alignItems={"flex-start"}
                                        xs={12}
                                      >
                                        <Grid
                                          item
                                          sx={{
                                            width: "100%",
                                          }}
                                        >
                                          <Bar
                                            tooltip={`R$ ${row.goalValue.toLocaleString(
                                              "pt-BR"
                                            )}`}
                                            color={"#F98561"}
                                            completed={
                                              (row.goalValue / highestValue) *
                                              100
                                            }
                                          ></Bar>
                                        </Grid>
                                        <Grid
                                          item
                                          sx={{
                                            width: "100%",
                                          }}
                                        >
                                          <Bar
                                            tooltip={`R$ ${row.value.toLocaleString(
                                              "pt-BR"
                                            )}`}
                                            color={"#55B6EB"}
                                            completed={
                                              (row.value / highestValue) * 100
                                            }
                                          ></Bar>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                ))
                              : null}
                          </Grid>
                        </Grid>
                        {scale?.length && (
                          <Grid
                            item
                            container
                            flexDirection={"column"}
                            justifyContent={"center"}
                            rowGap={0.5}
                            xs
                          >
                            <Divider />

                            <Grid
                              container
                              flexDirection={"row"}
                              justifyContent={"space-between"}
                              alignItems={"center"}
                            >
                              {scale?.map((item, index) => (
                                <Grid
                                  key={index}
                                  item
                                  container
                                  xs
                                  flexDirection={"row"}
                                  justifyContent={
                                    index === 2 ? "flex-end" : "flex-start"
                                  }
                                >
                                  <Box
                                    component={"span"}
                                    sx={{
                                      fontWeight: "400",
                                      color: "#D3D3D3",
                                    }}
                                  >
                                    {item.toLocaleString("pt-BR")}
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Grid
                container
                flexDirection={"flex-row"}
                justifyContent={"flex-start"}
                columnGap={2}
              >
                <Grid item xs sx={{ height: "100%" }}>
                  <Card
                    app={app}
                    header={{
                      hardTitle: "Relatórios",
                    }}
                    exportTable
                    exportTableId={"cUpjCX"}
                    exportContainer
                    style={{ height: "100%" }}
                  >
                    <Grid
                      item
                      sx={{
                        width: "100%",
                        height: "100%",
                        minHeight: notFullHd ? "69vh" : "73vh",
                      }}
                      xs
                    >
                      <NativeObject
                        app={app}
                        key="cUpjCX"
                        type={"table"}
                        reference={{
                          id: "cUpjCX",
                        }}
                        style={{
                          padding: "0 8px",
                        }}
                      />
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Grid>
        </LayoutWrapper>
      </div>
    </div>
  );
}

ExtraScoresDesktop.defaultProps = {
  app: null,
  config: {},
};

ExtraScoresDesktop.propTypes = {
  app: PropTypes.object,
  config: PropTypes.object,
};

export default ExtraScoresDesktop;
